import React, { useState } from 'react';

export const MAX_INVITES = 20; // Matches MAX_INVITES_PER_EVENT on the server.

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidInviteEmail(value) {
  return EMAIL_PATTERN.test(String(value || '').trim());
}

// Adds any valid, new addresses from free text (commas, spaces, new lines).
// Returns the updated list plus anything that couldn't be added.
export function addInviteEmails(current, text) {
  const next = [...current];
  const rejected = [];
  let full = false;

  for (const part of String(text || '').split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (!email) continue;
    if (!isValidInviteEmail(email)) {
      rejected.push(part.trim());
    } else if (!next.includes(email)) {
      if (next.length >= MAX_INVITES) {
        full = true;
      } else {
        next.push(email);
      }
    }
  }

  return { emails: next, rejected, full };
}

// Optional email invites: type addresses (a friend's or your own) and press
// Enter, comma or space to add each one.
export default function InviteEmailsField({ emails, onChange, draft, onDraftChange, sendsRightAway = false }) {
  const [error, setError] = useState('');

  const commit = (text) => {
    const result = addInviteEmails(emails, text);
    onChange(result.emails);
    onDraftChange(result.rejected.join(' '));
    if (result.rejected.length) {
      setError(`“${result.rejected[0]}” doesn’t look like an email address.`);
    } else if (result.full) {
      setError(`You can invite up to ${MAX_INVITES} people per event.`);
    } else {
      setError('');
    }
  };

  const onKeyDown = (event) => {
    if (['Enter', ',', ' ', 'Tab'].includes(event.key) && draft.trim()) {
      // Enter would otherwise submit the whole form.
      event.preventDefault();
      commit(draft);
    } else if (event.key === 'Enter') {
      event.preventDefault();
    } else if (event.key === 'Backspace' && !draft && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div className="invite-field">
      <label htmlFor="invite-emails" className="invite-label">
        Send invites (optional)
      </label>

      <div className="invite-box">
        {emails.map((email) => (
          <span key={email} className="invite-chip">
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              onClick={() => onChange(emails.filter((item) => item !== email))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id="invite-emails"
          type="email"
          inputMode="email"
          autoComplete="off"
          value={draft}
          onChange={(event) => {
            const value = event.target.value;
            // Pasting a list adds everything at once.
            if (/[\s,;]/.test(value)) {
              commit(value);
            } else {
              onDraftChange(value);
              setError('');
            }
          }}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && commit(draft)}
          placeholder={emails.length ? 'Add another email' : 'Friend’s email, or your own'}
          disabled={emails.length >= MAX_INVITES}
        />
      </div>

      {error ? (
        <div className="invite-hint is-error" role="alert">{error}</div>
      ) : (
        <div className="invite-hint">
          {sendsRightAway
            ? 'We’ll email each person a link as soon as you create the event.'
            : 'We’ll email each person a link once your event is approved.'}{' '}
          Addresses are only used for this invite.
        </div>
      )}
    </div>
  );
}
